import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChartLine, Users, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

function KpiCard({ label, value, testid }) {
  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid={testid}>
      <div className="gf-overline text-zinc-500">{label}</div>
      <div className="mt-2 font-display font-black text-3xl tracking-tight text-zinc-900">
        {value}
      </div>
    </div>
  );
}

export default function AdminAttribution() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/attribution/summary");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load attribution data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="p-8 text-zinc-500 font-mono text-sm" data-testid="attribution-loading">
        Loading attribution…
      </div>
    );
  }
  if (!data) return null;

  const { totals, by_source, by_campaign, recent_signups } = data;

  return (
    <div className="min-h-screen bg-zinc-50 p-6 md:p-10" data-testid="admin-attribution-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="gf-overline text-zinc-500 flex items-center gap-2">
              <ChartLine size={14} weight="bold" />
              Marketing Attribution
            </div>
            <h1 className="font-display font-black text-4xl tracking-tight text-zinc-900 mt-2">
              Where your customers come from.
            </h1>
            <p className="text-sm text-zinc-600 mt-2 max-w-xl">
              First-touch attribution captured from UTM tags, Facebook <code className="font-mono text-xs bg-zinc-100 px-1">fbclid</code>,
              Google Ads <code className="font-mono text-xs bg-zinc-100 px-1">gclid</code>, and HTTP referrer.
              A user&apos;s first landing URL wins credit for their eventual signup + purchases.
            </p>
          </div>
          <Button
            onClick={load}
            variant="outline"
            className="gf-sharp"
            data-testid="attribution-refresh"
          >
            <ArrowsCounterClockwise size={16} className="mr-2" />
            Refresh
          </Button>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <KpiCard label="Total signups" value={totals.total_signups} testid="kpi-signups" />
          <KpiCard label="Paying customers" value={totals.total_paid_users} testid="kpi-paid-users" />
          <KpiCard label="Paid transactions" value={totals.total_paid_transactions} testid="kpi-paid-txs" />
          <KpiCard label="Total revenue" value={fmtMoney(totals.total_revenue_usd)} testid="kpi-revenue" />
        </div>

        {/* By-source table */}
        <section className="bg-white border border-zinc-200 mb-8" data-testid="table-by-source">
          <div className="border-b border-zinc-200 p-4 flex items-center gap-2">
            <Users size={16} weight="bold" className="text-zinc-500" />
            <div className="gf-overline text-zinc-900">By source</div>
          </div>
          {by_source.length === 0 ? (
            <div className="p-8 text-sm text-zinc-500 font-mono">No signups tracked yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-500 uppercase font-mono text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">Source</th>
                    <th className="text-right px-4 py-3">Signups</th>
                    <th className="text-right px-4 py-3">Paid users</th>
                    <th className="text-right px-4 py-3">Conversion</th>
                    <th className="text-right px-4 py-3">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {by_source.map((row) => (
                    <tr key={row.source} className="border-t border-zinc-100" data-testid={`row-src-${row.source}`}>
                      <td className="px-4 py-3 font-mono text-zinc-900">{row.source}</td>
                      <td className="px-4 py-3 text-right">{row.signups}</td>
                      <td className="px-4 py-3 text-right">{row.paid_users}</td>
                      <td className="px-4 py-3 text-right">{fmtPct(row.conversion_rate)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmtMoney(row.revenue_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* By-campaign table */}
        <section className="bg-white border border-zinc-200 mb-8" data-testid="table-by-campaign">
          <div className="border-b border-zinc-200 p-4">
            <div className="gf-overline text-zinc-900">By campaign · medium</div>
          </div>
          {by_campaign.length === 0 ? (
            <div className="p-8 text-sm text-zinc-500 font-mono">No campaign-tagged traffic yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-500 uppercase font-mono text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">Source</th>
                    <th className="text-left px-4 py-3">Campaign</th>
                    <th className="text-left px-4 py-3">Medium</th>
                    <th className="text-right px-4 py-3">Signups</th>
                    <th className="text-right px-4 py-3">Paid</th>
                    <th className="text-right px-4 py-3">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {by_campaign.map((row, i) => (
                    <tr key={i} className="border-t border-zinc-100">
                      <td className="px-4 py-3 font-mono text-zinc-900">{row.source}</td>
                      <td className="px-4 py-3">{row.campaign}</td>
                      <td className="px-4 py-3 text-zinc-600">{row.medium}</td>
                      <td className="px-4 py-3 text-right">{row.signups}</td>
                      <td className="px-4 py-3 text-right">{row.paid_users}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmtMoney(row.revenue_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Recent signups */}
        <section className="bg-white border border-zinc-200" data-testid="table-recent-signups">
          <div className="border-b border-zinc-200 p-4">
            <div className="gf-overline text-zinc-900">25 most-recent signups</div>
          </div>
          {recent_signups.length === 0 ? (
            <div className="p-8 text-sm text-zinc-500 font-mono">No signups yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-500 uppercase font-mono text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">Signed up</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Source</th>
                    <th className="text-left px-4 py-3">Campaign</th>
                    <th className="text-left px-4 py-3">Referrer</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_signups.map((u) => (
                    <tr key={u.email} className="border-t border-zinc-100">
                      <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                        {u.created_at ? new Date(u.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3">{u.email}</td>
                      <td className="px-4 py-3 font-mono">{u.source}</td>
                      <td className="px-4 py-3">{u.campaign}</td>
                      <td className="px-4 py-3 text-xs text-zinc-500 max-w-xs truncate">
                        {u.attribution?.referrer || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="text-xs text-zinc-500 font-mono mt-6">
          Data updates in real time. Tag your links like <code>?utm_source=twitter&amp;utm_campaign=launch</code> to see them here.
        </p>
      </div>
    </div>
  );
}
